/**
 * "A full garden, even with a full life.": three short cards for the gardener
 * who plants first, feels overwhelmed, or keeps losing plants.
 *
 * Words (en/es): landing.life.*.
 *
 * @module FullLife
 */

import React from 'react';
import { ListChecks, Shovel, Flower2, type LucideIcon } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { LandingBlock } from '../components/LandingBlock';

/** One card: its icon and its words (translation keys). */
interface Card {
  /** The card's key. */
  id: string;
  /** Its icon. */
  Icon: LucideIcon;
  /** Its heading. */
  title: string;
  /** Its sentence. */
  body: string;
}

/** The three cards, in order. */
const CARDS: readonly Card[] = [
  { id: 'plant', Icon: Shovel, title: 'landing.life.plant.title', body: 'landing.life.plant.body' },
  { id: 'overwhelmed', Icon: ListChecks, title: 'landing.life.overwhelmed.title', body: 'landing.life.overwhelmed.body' },
  { id: 'losing', Icon: Flower2, title: 'landing.life.losing.title', body: 'landing.life.losing.body' },
];

/** The "full life" section. */
export const FullLife: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  return (
    <LandingBlock title={t('landing.life.title')} single>
      <div className="grid gap-3 md:grid-cols-3">
        {CARDS.map(({ id, Icon, title, body }) => (
          <div key={id} className="space-y-2 rounded-2xl border bg-card p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <Icon className="h-9 w-9 shrink-0 text-primary" strokeWidth={1.75} aria-hidden="true" />
              <h3 className="min-w-0 font-grow text-lg font-bold">{t(title)}</h3>
            </div>
            <p>{t(body)}</p>
          </div>
        ))}
      </div>
    </LandingBlock>
  );
};
